import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { useMe } from '@/auth/useMe';
import { errorMessage } from '@/lib/errors';
import { useOpenDirectChannel } from './admin/api';

/**
 * פתיחת שיחה אישית מול עובד.
 *
 * אינה יוצרת שיחה חדשה אם כבר יש אחת: הפונקציה בשרת מחזירה את
 * הקיימת. לכן הכפתור אומר "שיחה אישית" ולא "פתיחת שיחה" — מה
 * שקורה הוא מעבר לשיחה, בין שהיא כבר הייתה ובין שלא.
 */
export function DirectMessageButton({
  employeeId,
  to,
}: {
  employeeId: string;
  /** לאן לנווט. בקונסולה /admin/channels, באפליקציה /messages. */
  to: 'admin' | 'app';
}) {
  const navigate = useNavigate();
  const { me } = useMe();
  const canDirect = usePerm('messages.direct');
  const open = useOpenDirectChannel();
  const [error, setError] = useState<string | null>(null);

  if (!canDirect || me?.employeeId === employeeId) return null;

  return (
    <>
      <Button
        variant="secondary"
        disabled={open.isPending}
        onClick={() => {
          setError(null);
          open
            .mutateAsync(employeeId)
            .then((channelId) =>
              navigate(to === 'admin' ? `/admin/channels/${channelId}` : `/messages/${channelId}`),
            )
            .catch((caught: unknown) => setError(errorMessage(caught)));
        }}
      >
        <MessageCircle size={18} strokeWidth={1.75} aria-hidden="true" />
        שיחה אישית
      </Button>
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}
    </>
  );
}
